export type DataEnvironment = "dev" | "prod";

export type EndpointProfile = {
  apiBaseUrl: string;
  weatherAgentUrl: string;
  monitoringApiUrl: string;
  weatherAgentWsUrl: string;
};

export type WebfrontRuntimeConfig = {
  stack: "local" | "cloud";
  allowMixedEndpoints: boolean;
  apiBaseUrl: string;
  weatherAgentUrl: string;
  monitoringApiUrl: string;
  weatherAgentWsUrl: string;
  endpointProfiles: Record<DataEnvironment, EndpointProfile>;
  initialDataEnvironment: DataEnvironment;
  publishTargetEnvironment: "prod";
};

type RuntimeEnv = Record<string, string | undefined>;

const DEFAULT_CLOUD_ENDPOINTS = {
  apiBaseUrl: "https://api.greensparks.live",
  weatherAgentUrl: "https://agent-api.greensparks.live",
  monitoringApiUrl: "https://agent-api.greensparks.live",
  weatherAgentWsUrl: "wss://agent-api.greensparks.live/ws",
};

const COMPILED_STACK = process.env.REACT_APP_STACK;
const DEFAULT_LOCAL_ENDPOINTS =
  COMPILED_STACK === "cloud"
    ? DEFAULT_CLOUD_ENDPOINTS
    : {
        apiBaseUrl: "http://localhost:8000",
        weatherAgentUrl: "http://localhost:8005",
        monitoringApiUrl: "http://localhost:8005",
        weatherAgentWsUrl: "ws://localhost:8005/ws",
      };

const LOCAL_HOSTNAMES = new Set(["localhost", "127.0.0.1", "::1"]);

const normalizeBoolean = (value: string | undefined) =>
  String(value || "").trim().toLowerCase() === "true";

const resolveDataEnvironment = (value: string | undefined, fallback: DataEnvironment): DataEnvironment => {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) {
    return fallback;
  }
  if (raw === "dev" || raw === "prod") {
    return raw;
  }
  throw new Error(
    `Invalid data environment '${raw}'. Expected REACT_APP_INITIAL_DATA_ENVIRONMENT to be 'dev' or 'prod'.`
  );
};

const resolveStack = (env: RuntimeEnv): "local" | "cloud" => {
  const raw = (env.REACT_APP_STACK || env.REACT_APP_ENV || "local").trim().toLowerCase();
  if (raw === "local" || raw === "cloud") {
    return raw;
  }
  throw new Error(
    `Invalid webfront stack '${raw}'. Expected REACT_APP_STACK to be 'local' or 'cloud'.`
  );
};

const classifyEndpoint = (url: string): "local" | "cloud" | "unknown" => {
  try {
    const parsed = new URL(url);
    return LOCAL_HOSTNAMES.has(parsed.hostname) ? "local" : "cloud";
  } catch (_error) {
    return "unknown";
  }
};

const resolveEndpointProfile = (
  env: RuntimeEnv,
  prefix: "DEV" | "PROD",
  defaults: EndpointProfile
): EndpointProfile => {
  const apiBaseUrl = env[`REACT_APP_${prefix}_API_BASE_URL`] || defaults.apiBaseUrl;
  const weatherAgentUrl =
    env[`REACT_APP_${prefix}_WEATHER_AGENT_URL`] || defaults.weatherAgentUrl;
  const monitoringApiUrl =
    env[`REACT_APP_${prefix}_MONITORING_API_URL`] ||
    env[`REACT_APP_${prefix}_WEATHER_AGENT_URL`] ||
    defaults.monitoringApiUrl;
  const weatherAgentWsUrl =
    env[`REACT_APP_${prefix}_WEATHER_AGENT_WS_URL`] ||
    `${weatherAgentUrl.replace(/^http/, "ws")}/ws`;

  return {
    apiBaseUrl,
    weatherAgentUrl,
    monitoringApiUrl,
    weatherAgentWsUrl,
  };
};

const assertConsistentEndpointFamilies = ({
  stack,
  allowMixedEndpoints,
  endpoints,
}: {
  stack: "local" | "cloud";
  allowMixedEndpoints: boolean;
  endpoints: Array<{ label: string; url: string }>;
}) => {
  const classifications = endpoints.map((entry) => ({
    ...entry,
    family: classifyEndpoint(entry.url),
  }));

  const invalid = classifications.filter((entry) => entry.family === "unknown");
  if (invalid.length > 0) {
    throw new Error(
      `Invalid webfront endpoint URL(s): ${invalid
        .map((entry) => `${entry.label}=${entry.url}`)
        .join(", ")}`
    );
  }

  if (allowMixedEndpoints) {
    return;
  }

  const mismatched = classifications.filter((entry) => entry.family !== stack);
  if (mismatched.length > 0) {
    throw new Error(
      `Mixed webfront endpoints are not allowed for stack '${stack}'. ` +
        `Mismatched endpoint(s): ${mismatched
          .map((entry) => `${entry.label}=${entry.url}`)
          .join(", ")}. ` +
        `Set REACT_APP_ALLOW_MIXED_ENDPOINTS=true only for deliberate expert debugging.`
    );
  }
};

export const getWebfrontRuntimeConfig = (
  env: RuntimeEnv = process.env as RuntimeEnv
): WebfrontRuntimeConfig => {
  const stack = resolveStack(env);
  const defaults = stack === "local" ? DEFAULT_LOCAL_ENDPOINTS : DEFAULT_CLOUD_ENDPOINTS;
  const apiBaseUrl = env.REACT_APP_API_BASE_URL || defaults.apiBaseUrl;
  const weatherAgentUrl = env.REACT_APP_WEATHER_AGENT_URL || defaults.weatherAgentUrl;
  const monitoringApiUrl =
    env.REACT_APP_MONITORING_API_URL || env.REACT_APP_WEATHER_AGENT_URL || defaults.monitoringApiUrl;
  const weatherAgentWsUrl =
    env.REACT_APP_WEATHER_AGENT_WS_URL ||
    `${weatherAgentUrl.replace(/^http/, "ws")}/ws`;
  const allowMixedEndpoints = normalizeBoolean(env.REACT_APP_ALLOW_MIXED_ENDPOINTS);

  assertConsistentEndpointFamilies({
    stack,
    allowMixedEndpoints,
    endpoints: [
      { label: "REACT_APP_API_BASE_URL", url: apiBaseUrl },
      { label: "REACT_APP_WEATHER_AGENT_URL", url: weatherAgentUrl },
      { label: "REACT_APP_MONITORING_API_URL", url: monitoringApiUrl },
      { label: "REACT_APP_WEATHER_AGENT_WS_URL", url: weatherAgentWsUrl },
    ],
  });

  const prodEndpointProfile = resolveEndpointProfile(
    env,
    "PROD",
    stack === "cloud"
      ? { apiBaseUrl, weatherAgentUrl, monitoringApiUrl, weatherAgentWsUrl }
      : DEFAULT_CLOUD_ENDPOINTS
  );
  const endpointProfiles = {
    dev:
      stack === "cloud"
        ? prodEndpointProfile
        : resolveEndpointProfile(env, "DEV", DEFAULT_LOCAL_ENDPOINTS),
    prod: prodEndpointProfile,
  };

  assertConsistentEndpointFamilies({
    stack: stack === "cloud" ? "cloud" : "local",
    allowMixedEndpoints,
    endpoints: [
      { label: "REACT_APP_DEV_API_BASE_URL", url: endpointProfiles.dev.apiBaseUrl },
      { label: "REACT_APP_DEV_WEATHER_AGENT_URL", url: endpointProfiles.dev.weatherAgentUrl },
      { label: "REACT_APP_DEV_MONITORING_API_URL", url: endpointProfiles.dev.monitoringApiUrl },
      { label: "REACT_APP_DEV_WEATHER_AGENT_WS_URL", url: endpointProfiles.dev.weatherAgentWsUrl },
    ],
  });

  assertConsistentEndpointFamilies({
    stack: "cloud",
    allowMixedEndpoints: false,
    endpoints: [
      { label: "REACT_APP_PROD_API_BASE_URL", url: endpointProfiles.prod.apiBaseUrl },
      { label: "REACT_APP_PROD_WEATHER_AGENT_URL", url: endpointProfiles.prod.weatherAgentUrl },
      { label: "REACT_APP_PROD_MONITORING_API_URL", url: endpointProfiles.prod.monitoringApiUrl },
      { label: "REACT_APP_PROD_WEATHER_AGENT_WS_URL", url: endpointProfiles.prod.weatherAgentWsUrl },
    ],
  });

  const requestedInitialDataEnvironment = resolveDataEnvironment(
    env.REACT_APP_INITIAL_DATA_ENVIRONMENT,
    stack === "local" ? "dev" : "prod"
  );
  const initialDataEnvironment =
    stack === "cloud" ? "prod" : requestedInitialDataEnvironment;

  return {
    stack,
    allowMixedEndpoints,
    apiBaseUrl,
    weatherAgentUrl,
    monitoringApiUrl,
    weatherAgentWsUrl,
    endpointProfiles,
    initialDataEnvironment,
    publishTargetEnvironment: "prod",
  };
};

export const WEBFRONT_RUNTIME_CONFIG = getWebfrontRuntimeConfig();
